import React, { useEffect, useMemo, useRef, useState } from "react";
import { toUrl } from "../../lib/config";

type Partner = {
  src: string;
  alt: string;
  href?: string;
};

type PartnersCarouselProps = {
  titleLight?: string;          // "Anlaşmalı"
  titleAccent?: string;         // "Kurumlar"
  partners: Partner[];
  intervalMs?: number;          // default 2000
  accentColor?: string;         // default "#2cadd2"
  visibleDesktop?: number;      // >=1024px => default 3
  visibleTablet?: number;       // 640–1023 => default 3
  visibleMobile?: number;       // <640    => default 2
};

const PartnersCarousel: React.FC<PartnersCarouselProps> = ({
  titleLight = "Anlaşmalı",
  titleAccent = "Kurumlar",
  partners,
  intervalMs = 2000,
  accentColor = "#2cadd2",
  visibleDesktop = 3,  // 3 logo görünsün
  visibleTablet = 3,
  visibleMobile = 2,
}) => {
  const ImgWithFallback: React.FC<{ src?: string; alt?: string }> = ({ src, alt }) => {
    const input = src || "";
    const raw = /^https?:\/\//i.test(input)
      ? input
      : (input.startsWith('/api/') ? input : (input ? `/api/media/${input}` : ''));
    const abs = /^https?:\/\//i.test(raw) ? raw : toUrl(raw);
    const [current, setCurrent] = useState(abs);
    return (
      <img
        src={current}
        alt={alt || ''}
        style={{ width: 160, height: 160, objectFit: 'contain', display: 'block', WebkitBackfaceVisibility: 'hidden', backfaceVisibility: 'hidden', transform: 'translateZ(0)', willChange: 'transform' }}
        crossOrigin="anonymous"
        loading="eager"
        onError={(e) => {
          const cur = e.currentTarget.getAttribute('src') || '';
          const next = cur === abs ? raw : abs;
          if (next && next !== cur) e.currentTarget.src = next;
        }}
      />
    );
  };



  // Ekrana göre görünür adet
  const getVisible = () => {
    if (typeof window === "undefined") return visibleDesktop;
    const w = window.innerWidth;
    if (w < 640) return visibleMobile;
    if (w < 1024) return visibleTablet;
    return visibleDesktop;
  };

  const [visible, setVisible] = useState<number>(getVisible());
  useEffect(() => {
    const onResize = () => setVisible(getVisible());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Kayacak kadar eleman yoksa hiç animasyon yapmayız
  const canSlide = partners.length > visible;

  // Sonsuz akış için tam listeyi bir kez daha ekleyip buffer'ı artırıyoruz
  const extended = useMemo(
    () => (canSlide ? [...partners, ...partners] : partners),
    [partners, canSlide]
  );

  const [index, setIndex] = useState(0); // kaç item sola kaydı
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const trackRef = useRef<HTMLDivElement | null>(null);

  // Hover'da durdur
  const isHoverRef = useRef(false);
  const onMouseEnter = () => (isHoverRef.current = true);
  const onMouseLeave = () => (isHoverRef.current = false);

  // Oynatıcı
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    clearTimer();
    if (!canSlide) return;

    timerRef.current = window.setInterval(() => {
      if (!isHoverRef.current) setIndex((i) => i + 1);
    }, intervalMs);

    return () => { clearTimer(); };
  }, [intervalMs, canSlide]);

  // Sonsuz akış bitişini yakalayıp başa al
  useEffect(() => {
    if (!canSlide) return;

    const el = trackRef.current;
    if (!el) return;

    const onEnd = () => {
      if (index === partners.length) {
        // en sona kadar kaydı; başa sar
        setTransitionEnabled(false);
        setIndex(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setTransitionEnabled(true));
        });
      }
    };
    el.addEventListener("transitionend", onEnd);
    return () => el.removeEventListener("transitionend", onEnd);
  }, [index, partners.length, canSlide]);

  // Butonlar
  const next = () => {
    if (!canSlide) return;
    setIndex((i) => i + 1);
  };

  const prev = () => {
    if (!canSlide) return;

    if (index === 0) {
      // baştayken sarsıntısız şekilde sondaki pozisyona ışınla
      setTransitionEnabled(false);
      setIndex(partners.length);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
          setIndex(partners.length - 1);
        });
      });
    } else {
      setIndex((i) => i - 1);
    }
  };

  // Her öğe genişliği = 100 / visible
  const itemWidthPct = 100 / visible;
  const translateX = -(index * itemWidthPct);

  // CSS custom properties
  const styleVars: React.CSSProperties = {
    // @ts-ignore - CSS var override
    "--pc-accent": accentColor,
    // @ts-ignore
    "--pc-item-w": `${itemWidthPct}%`,
  };

  return (
    <section className="partners-carousel" style={styleVars}>
      <div className="container">
        <header className="pc-head">
          <h2 className="pc-title">
            <span className="light">{titleLight}</span>{" "}
            <span className="accent">{titleAccent}</span>
          </h2>
          <span className="pc-underline" />
        </header>

        <div
          className="pc-viewport"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {/* Oklar logoların ÜSTÜNE gelmesin: dışa konumlandırıldı */}
          <button
            className="pc-nav prev"
            aria-label="Önceki"
            type="button"
            onClick={prev}
          >
            <i className="bi bi-chevron-left" />
          </button>

          <div className="pc-track-wrap">
            <div
              ref={trackRef}
              className="pc-track"
              style={{
                transform: `translate3d(${translateX}%, 0, 0)`,
                transition: transitionEnabled ? "transform 450ms ease" : "none",
                willChange: 'transform',
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden',
              }}
            >
              {extended.map((p, i) => (
                <div className="pc-item" key={`${p.alt}-${i}-${p.src}`} style={{ width: `${itemWidthPct}%`, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', contain: 'content' }}>
                  {p.href ? (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noreferrer"
                      className="pc-logo"
                      style={{ width: 180, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <ImgWithFallback src={p.src} alt={p.alt} />
                    </a>
                  ) : (
                    <div className="pc-logo" style={{ width: 180, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImgWithFallback src={p.src} alt={p.alt} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            className="pc-nav next"
            aria-label="Sonraki"
            type="button"
            onClick={next}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </div>

        {/* (opsiyonel) CTA */}
        <div className="pc-cta">
          <a className="pc-btn" href="/anlasmali-sigortalar">
            Tüm Anlaşmalı Kurumlar <i className="bi bi-chevron-right" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default PartnersCarousel;
